import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { QuoteSelector } from './quote-selector';
import { ComparisonCard } from './comparison-card';
import { useAiIntelligenceStore } from '../store/ai-intelligence-store';
import { Play, RotateCcw, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function SimulatorTab() {
  const [quoteId, setQuoteId] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  const [quantityMultiplier, setQuantityMultiplier] = useState('');
  const [paymentType, setPaymentType] = useState('');

  const { simulate, simulationResult, isSimulating, simulationError, clearSimulation } =
    useAiIntelligenceStore();

  const handleSimulate = () => {
    if (!quoteId) return;
    simulate({
      quoteId,
      discountPercent: discountPercent ? parseFloat(discountPercent) : undefined,
      priceOverride: priceOverride ? parseFloat(priceOverride) : undefined,
      quantityMultiplier: quantityMultiplier ? parseFloat(quantityMultiplier) : undefined,
      paymentType: paymentType || undefined,
    });
  };

  const handleReset = () => {
    setDiscountPercent('');
    setPriceOverride('');
    setQuantityMultiplier('');
    setPaymentType('');
    clearSimulation();
  };

  const result = simulationResult;

  // Chart data for comparison
  const chartData = result
    ? [
        {
          name: 'Costo Base',
          original: result.original.costoBase,
          simulado: result.simulado.costoBase,
        },
        {
          name: 'Precio Sugerido',
          original: result.original.precioSugerido,
          simulado: result.simulado.precioSugerido,
        },
        {
          name: 'Precio Final',
          original: result.original.precioFinal,
          simulado: result.simulado.precioFinal,
        },
      ]
    : [];

  const approvalBadge = result
    ? {
        APROBACION_AUTOMATICA: { label: 'Aprobación Automática', variant: 'success' as const },
        REQUIERE_APROBACION: { label: 'Requiere Aprobación', variant: 'warning' as const },
        BAJO_PISO: { label: 'Bajo Piso Mínimo', variant: 'danger' as const },
      }[result.estadoAprobacion] || { label: result.estadoAprobacion, variant: 'warning' as const }
    : undefined;

  return (
    <div className="space-y-6">
      {/* Configuración */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configurar Simulación</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Selecciona una cotización existente y modifica uno o más parámetros para ver como
            cambiaria el resultado. La cotizacion original{' '}
            <strong className="text-foreground">no se modifica</strong> — esto es solo una
            simulacion.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <QuoteSelector value={quoteId} onChange={setQuoteId} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Descuento (%)</label>
              <Input
                type="number"
                placeholder="Ej: 10"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                min={0}
                max={100}
                step={0.5}
              />
              <p className="text-xs text-muted-foreground">
                Porcentaje de descuento sobre el precio sugerido
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Precio Override ($)</label>
              <Input
                type="number"
                placeholder="Ej: 50000"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
                min={0}
                step={100}
              />
              <p className="text-xs text-muted-foreground">
                Forzar un precio final especifico (ignora el descuento)
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Multiplicador Cantidad</label>
              <Input
                type="number"
                placeholder="Ej: 2"
                value={quantityMultiplier}
                onChange={(e) => setQuantityMultiplier(e.target.value)}
                min={0.1}
                step={0.5}
              />
              <p className="text-xs text-muted-foreground">
                2 = duplicar cantidades, 0.5 = reducir a la mitad
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Tipo de Pago</label>
              <SearchableSelect
                value={paymentType}
                onValueChange={(val) => setPaymentType(val)}
                options={[
                  { value: '', label: 'Sin cambio' },
                  { value: 'CASH', label: 'Contado' },
                  { value: 'CREDIT_30', label: 'Crédito 30 días' },
                  { value: 'CREDIT_60', label: 'Crédito 60 días' },
                  { value: 'CREDIT_90', label: 'Crédito 90 días' },
                  { value: 'INSTALLMENTS', label: 'Cuotas' },
                ]}
                placeholder="Sin cambio"
              />
              <p className="text-xs text-muted-foreground">Simula condiciones de pago diferentes</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSimulate} disabled={!quoteId || isSimulating}>
              <Play className="w-4 h-4 mr-2" />
              {isSimulating ? 'Simulando...' : 'Simular'}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {isSimulating && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      )}

      {/* Error */}
      {simulationError && (
        <Card className="border-destructive/50">
          <CardContent className="pt-6 flex items-center gap-3 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <p>{simulationError}</p>
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {result && !isSimulating && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Comparación numérica */}
            <ComparisonCard
              title={`${result.cotizacion} — Comparación`}
              badge={approvalBadge}
              values={[
                {
                  label: 'Costo Base',
                  original: result.original.costoBase,
                  simulated: result.simulado.costoBase,
                  format: 'currency',
                  currency: result.moneda,
                },
                {
                  label: 'Precio Sugerido',
                  original: result.original.precioSugerido,
                  simulated: result.simulado.precioSugerido,
                  format: 'currency',
                  currency: result.moneda,
                },
                {
                  label: 'Precio Final',
                  original: result.original.precioFinal,
                  simulated: result.simulado.precioFinal,
                  format: 'currency',
                  currency: result.moneda,
                },
              ]}
            />

            {/* Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Comparación Visual</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [`$${Number(value).toLocaleString()}`, '']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    />
                    <Bar
                      dataKey="original"
                      name="Original"
                      fill="hsl(var(--muted-foreground))"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="simulado"
                      name="Simulado"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Métricas adicionales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Margen Original" value={result.original.margenReal} />
            <MetricCard label="Margen Simulado" value={result.simulado.margenReal} />
            <MetricCard label="Margen Objetivo" value={result.margenObjetivo} />
            <MetricCard label="Margen Piso" value={result.margenPiso} />
          </div>

          {/* Diferencias */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumen de Diferencias</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-1">Diferencia Precio Final</p>
                  <p className="text-lg font-bold">
                    ${result.diferencia.precioFinal.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-1">Diferencia Margen</p>
                  <p className="text-lg font-bold">{result.diferencia.margen}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-1">Descuento Aplicado</p>
                  <p className="text-lg font-bold">{result.simulado.descuentoAplicado}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty state */}
      {!result && !isSimulating && !simulationError && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground py-12">
            <Play className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-1">Simulador de Escenarios What-If</p>
            <p className="text-sm max-w-lg mx-auto">
              Selecciona una cotizacion arriba y modifica al menos un parametro (descuento, precio,
              cantidad o pago). Veras una comparacion lado a lado entre el escenario original y el
              simulado, con gráficos y el impacto en margen.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg border bg-card text-center">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
